export const REMOTE_SERVICE_UUID = "7f5a0001-6c8d-4f75-9b54-0e3a9c2d10e9";
export const PROTOCOL_INFO_UUID = "7f5a0002-6c8d-4f75-9b54-0e3a9c2d10e9";
export const COMMAND_UUID = "7f5a0003-6c8d-4f75-9b54-0e3a9c2d10e9";
export const EVENT_UUID = "7f5a0004-6c8d-4f75-9b54-0e3a9c2d10e9";
export const LIVE_STREAM_UUID = "7f5a0005-6c8d-4f75-9b54-0e3a9c2d10e9";

export const FRAME_MAGIC = 0xe9;
export const PROTOCOL_VERSION = 2;
export const APPLICATION_AUTH_FORMAT_VERSION = 1;
export const FRAME_HEADER_BYTES = 9;
export const FRAME_DATA_BYTES = 11;
export const MAX_MESSAGE_BYTES = 1024;

export const enum FrameFlag {
  Request = 1 << 0,
  Response = 1 << 1,
  Event = 1 << 2,
}

export const enum Opcode {
  GetState = 0x01,
  GetConfig = 0x02,
  SetConfig = 0x03,
  ListSnapshots = 0x04,
  Save = 0x05,
  Load = 0x06,
  Unload = 0x07,
  Rename = 0x08,
  Delete = 0x09,
  GetTrace = 0x0a,
  ClearGraph = 0x0b,
  RunDemo = 0x0c,
  ResetBatch = 0x0d,
  RepairStorage = 0x0e,
  ConfirmPending = 0x0f,
  CancelPending = 0x10,
  GetAuthChallenge = 0x11,
  Authenticate = 0x12,
  PatchConfig = 0x13,
  StateChanged = 0x80,
  OperationCompleted = 0x82,
  TraceChanged = 0x83,
  SnapshotsChanged = 0x84,
}

export const enum StatusCode {
  Ok = 0x00,
  Queued = 0x01,
  Busy = 0x02,
  Invalid = 0x03,
  NotFound = 0x04,
  Conflict = 0x05,
  StorageUnavailable = 0x06,
  ConfirmationRequired = 0x07,
  PersistencePending = 0x08,
  Unauthorized = 0x09,
}

export const enum TlvTag {
  Session = 0x01,
  Revision = 0x02,
  ExpectedSession = 0x03,
  ExpectedRevision = 0x04,
  Capabilities = 0x05,
  DeviceName = 0x06,
  Head = 0x07,
  TraceSource = 0x08,
  SnapshotId = 0x09,
  SnapshotName = 0x0a,
  Token = 0x0b,
  PendingOp = 0x0c,
  Persistence = 0x0d,
  SelectedSnapshotId = 0x0e,
  Lifetime = 0x0f,
  Batch = 0x10,
  StateFlags = 0x11,
  HeadState = 0x12,
  SnapshotSummary = 0x13,
  TraceMeta = 0x14,
  Ticks = 0x15,
  SettingRecord = 0x17,
  CorruptCount = 0x18,
  CompletedRequestId = 0x19,
  ResultStatus = 0x1a,
  Generation = 0x1b,
  ConfirmationExpiryMs = 0x1c,
  SettingValue = 0x20,
  ControllerId = 0x21,
  AuthChallenge = 0x22,
  AuthClientNonce = 0x23,
  AuthProof = 0x24,
  RemovedSnapshotId = 0x25,
}

export interface ProtocolFrame {
  opcode: number;
  flags: number;
  requestId: number;
  fragmentIndex: number;
  fragmentCount: number;
  payload: Uint8Array;
}

export interface ProtocolMessage {
  opcode: number;
  flags: number;
  requestId: number;
  payload: Uint8Array;
}

export function encodeFrame(frame: ProtocolFrame): Uint8Array {
  if (frame.payload.length > FRAME_DATA_BYTES) {
    throw new RangeError(`Frame payload exceeds ${FRAME_DATA_BYTES} bytes`);
  }
  if (frame.fragmentCount < 1 || frame.fragmentIndex >= frame.fragmentCount) {
    throw new RangeError("Invalid fragment position");
  }
  validateEnvelope(frame.flags, frame.requestId);
  if (frame.fragmentCount > 94) throw new RangeError("Fragment count exceeds protocol limit");
  if (frame.fragmentIndex + 1 < frame.fragmentCount && frame.payload.length !== FRAME_DATA_BYTES) {
    throw new RangeError("Non-final fragments must carry 11 bytes");
  }
  if (frame.fragmentCount > 1 && frame.fragmentIndex + 1 === frame.fragmentCount && frame.payload.length === 0) {
    throw new RangeError("Final fragment must not be empty");
  }

  const bytes = new Uint8Array(FRAME_HEADER_BYTES + frame.payload.length);
  const view = new DataView(bytes.buffer);
  bytes[0] = FRAME_MAGIC;
  bytes[1] = PROTOCOL_VERSION;
  bytes[2] = frame.opcode;
  bytes[3] = frame.flags;
  view.setUint16(4, frame.requestId, true);
  bytes[6] = frame.fragmentIndex;
  bytes[7] = frame.fragmentCount;
  bytes[8] = frame.payload.length;
  bytes.set(frame.payload, FRAME_HEADER_BYTES);
  return bytes;
}

export function decodeFrame(bytes: Uint8Array): ProtocolFrame {
  if (bytes.length < FRAME_HEADER_BYTES) throw new Error("Truncated frame header");
  if (bytes[0] !== FRAME_MAGIC) throw new Error("Unexpected frame magic");
  if (bytes[1] !== PROTOCOL_VERSION) throw new Error("Unsupported protocol version");

  const payloadBytes = bytes[8] ?? 0;
  if (payloadBytes > FRAME_DATA_BYTES || bytes.length !== FRAME_HEADER_BYTES + payloadBytes) {
    throw new Error("Invalid frame payload length");
  }
  const fragmentIndex = bytes[6] ?? 0;
  const fragmentCount = bytes[7] ?? 0;
  if (fragmentCount < 1 || fragmentIndex >= fragmentCount) {
    throw new Error("Invalid fragment position");
  }

  const flags = bytes[3] ?? 0;
  const requestId = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint16(4, true);
  validateEnvelope(flags, requestId);
  if (fragmentCount > 94) throw new Error("Fragment count exceeds protocol limit");
  if (fragmentIndex + 1 < fragmentCount && payloadBytes !== FRAME_DATA_BYTES) {
    throw new Error("Short non-final fragment");
  }
  if (fragmentCount > 1 && fragmentIndex + 1 === fragmentCount && payloadBytes === 0) {
    throw new Error("Empty final fragment");
  }

  return {
    opcode: bytes[2] ?? 0,
    flags,
    requestId,
    fragmentIndex,
    fragmentCount,
    payload: bytes.slice(FRAME_HEADER_BYTES),
  };
}

function validateEnvelope(flags: number, requestId: number): void {
  if (flags !== FrameFlag.Request && flags !== FrameFlag.Response && flags !== FrameFlag.Event) {
    throw new Error("Invalid message flag");
  }
  if (flags === FrameFlag.Event ? requestId !== 0 : requestId === 0) {
    throw new Error("Invalid request ID for message kind");
  }
}

export function fragmentMessage(message: ProtocolMessage): Uint8Array[] {
  if (message.payload.length > MAX_MESSAGE_BYTES) {
    throw new RangeError(`Message exceeds ${MAX_MESSAGE_BYTES} bytes`);
  }
  const count = Math.max(1, Math.ceil(message.payload.length / FRAME_DATA_BYTES));
  if (count > 0xff) throw new RangeError("Message needs too many fragments");

  return Array.from({ length: count }, (_, index) =>
    encodeFrame({
      opcode: message.opcode,
      flags: message.flags,
      requestId: message.requestId,
      fragmentIndex: index,
      fragmentCount: count,
      payload: message.payload.slice(index * FRAME_DATA_BYTES, (index + 1) * FRAME_DATA_BYTES),
    }),
  );
}

interface PendingAssembly {
  opcode: number;
  flags: number;
  requestId: number;
  fragmentCount: number;
  nextIndex: number;
  length: number;
  chunks: Uint8Array[];
  touchedAt: number;
}

export class FrameAssembler {
  readonly #timeoutMs: number;
  #pending?: PendingAssembly;

  constructor(timeoutMs = 2_000) {
    this.#timeoutMs = timeoutMs;
  }

  reset(): void {
    this.#pending = undefined;
  }

  push(frameBytes: Uint8Array, now = Date.now()): ProtocolMessage | undefined {
    let frame: ProtocolFrame;
    try {
      frame = decodeFrame(frameBytes);
    } catch (error) {
      this.reset();
      throw error;
    }
    if (this.#pending && now - this.#pending.touchedAt > this.#timeoutMs) this.reset();

    if (frame.fragmentIndex === 0) {
      // Protocol v2 defines fragment zero as an explicit assembly restart.
      // This lets the next complete response recover immediately if a prior
      // notification was lost, without cascading another response timeout.
      this.reset();
      this.#pending = {
        opcode: frame.opcode,
        flags: frame.flags,
        requestId: frame.requestId,
        fragmentCount: frame.fragmentCount,
        nextIndex: 0,
        length: 0,
        chunks: [],
        touchedAt: now,
      };
    }

    const pending = this.#pending;
    if (!pending) throw new Error("Fragment received without message start");
    if (
      pending.opcode !== frame.opcode ||
      pending.flags !== frame.flags ||
      pending.requestId !== frame.requestId ||
      pending.fragmentCount !== frame.fragmentCount ||
      pending.nextIndex !== frame.fragmentIndex
    ) {
      this.reset();
      throw new Error("Mixed or out-of-order fragments");
    }

    pending.chunks.push(frame.payload);
    pending.length += frame.payload.length;
    pending.nextIndex += 1;
    pending.touchedAt = now;
    if (pending.length > MAX_MESSAGE_BYTES) {
      this.reset();
      throw new Error("Assembled message is too large");
    }
    if (pending.nextIndex !== pending.fragmentCount) return undefined;

    const payload = new Uint8Array(pending.length);
    let offset = 0;
    for (const chunk of pending.chunks) {
      payload.set(chunk, offset);
      offset += chunk.length;
    }
    const message = {
      opcode: pending.opcode,
      flags: pending.flags,
      requestId: pending.requestId,
      payload,
    };
    this.reset();
    return message;
  }
}

export class BinaryWriter {
  #bytes: number[] = [];

  u8(value: number): this {
    this.#bytes.push(value & 0xff);
    return this;
  }

  u16(value: number): this {
    this.#bytes.push(value & 0xff, (value >>> 8) & 0xff);
    return this;
  }

  u32(value: number): this {
    this.#bytes.push(value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff);
    return this;
  }

  u64(value: bigint): this {
    for (let shift = 0n; shift < 64n; shift += 8n) this.#bytes.push(Number((value >> shift) & 0xffn));
    return this;
  }

  f32(value: number): this {
    const bytes = new Uint8Array(4);
    new DataView(bytes.buffer).setFloat32(0, value, true);
    return this.bytes(bytes);
  }

  i32(value: number): this {
    return this.u32(value >>> 0);
  }

  bytes(value: Uint8Array): this {
    this.#bytes.push(...value);
    return this;
  }

  text(value: string): this {
    const encoded = new TextEncoder().encode(value);
    if (encoded.length > 0xff) throw new RangeError("String exceeds 255 UTF-8 bytes");
    return this.u8(encoded.length).bytes(encoded);
  }

  build(): Uint8Array {
    return Uint8Array.from(this.#bytes);
  }
}

export class BinaryReader {
  readonly #view: DataView;
  readonly #bytes: Uint8Array;
  #offset = 0;

  constructor(bytes: Uint8Array) {
    this.#bytes = bytes;
    this.#view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  }

  get remaining(): number {
    return this.#bytes.length - this.#offset;
  }

  #need(count: number): void {
    if (this.remaining < count) throw new Error("Truncated binary payload");
  }

  u8(): number {
    this.#need(1);
    return this.#bytes[this.#offset++] ?? 0;
  }

  u16(): number {
    this.#need(2);
    const value = this.#view.getUint16(this.#offset, true);
    this.#offset += 2;
    return value;
  }

  u32(): number {
    this.#need(4);
    const value = this.#view.getUint32(this.#offset, true);
    this.#offset += 4;
    return value;
  }

  u64(): bigint {
    this.#need(8);
    const value = this.#view.getBigUint64(this.#offset, true);
    this.#offset += 8;
    return value;
  }

  f32(): number {
    this.#need(4);
    const value = this.#view.getFloat32(this.#offset, true);
    this.#offset += 4;
    return value;
  }

  i32(): number {
    this.#need(4);
    const value = this.#view.getInt32(this.#offset, true);
    this.#offset += 4;
    return value;
  }

  bytes(count: number): Uint8Array {
    this.#need(count);
    const value = this.#bytes.slice(this.#offset, this.#offset + count);
    this.#offset += count;
    return value;
  }

  text(): string {
    return new TextDecoder("utf-8", { fatal: true }).decode(this.bytes(this.u8()));
  }

  done(): void {
    if (this.remaining !== 0) throw new Error("Unexpected trailing payload bytes");
  }
}

export interface Tlv {
  tag: number;
  value: Uint8Array;
}

export function encodeTlvs(tlvs: readonly Tlv[]): Uint8Array {
  const writer = new BinaryWriter();
  for (const tlv of tlvs) {
    if (tlv.value.length > 0xffff) throw new RangeError("TLV value is too large");
    writer.u8(tlv.tag).u16(tlv.value.length).bytes(tlv.value);
  }
  const result = writer.build();
  if (result.length > MAX_MESSAGE_BYTES) throw new RangeError(`TLV payload exceeds ${MAX_MESSAGE_BYTES} bytes`);
  return result;
}

export function decodeTlvs(bytes: Uint8Array): Tlv[] {
  const reader = new BinaryReader(bytes);
  const tlvs: Tlv[] = [];
  while (reader.remaining > 0) {
    const tag = reader.u8();
    const length = reader.u16();
    tlvs.push({ tag, value: reader.bytes(length) });
  }
  return tlvs;
}

export const tlv = {
  u8(tag: number, value: number): Tlv {
    return { tag, value: new BinaryWriter().u8(value).build() };
  },
  u16(tag: number, value: number): Tlv {
    return { tag, value: new BinaryWriter().u16(value).build() };
  },
  u32(tag: number, value: number): Tlv {
    return { tag, value: new BinaryWriter().u32(value).build() };
  },
  u64(tag: number, value: bigint): Tlv {
    return { tag, value: new BinaryWriter().u64(value).build() };
  },
  text(tag: number, value: string): Tlv {
    const encoded = new TextEncoder().encode(value);
    return { tag, value: encoded };
  },
  raw(tag: number, value: Uint8Array): Tlv {
    return { tag, value };
  },
};

export function firstTlv(tlvs: readonly Tlv[], tag: number): Uint8Array | undefined {
  return tlvs.find((item) => item.tag === tag)?.value;
}
